import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import { ApiError } from '@/lib/http';
import { fetchLanguages } from '@/lib/languages';
import { fetchKhutbahReading } from '@/features/reader/api';
import { fetchLearning, type GlossedWord } from '@/features/reader/learningApi';
import { readingQueryKey } from '@/features/reader/types';
import { Flashcards } from './Flashcards';
import { countdown, daysUntil, readiness, readinessLine } from './week';

/**
 * Learn this Friday's khutbah.
 *
 * Organised around the week, not around panes. The ministries publish about
 * a week ahead, so this is a bounded, dated preparation for one event:
 *
 *   It is Tuesday. Friday's khutbah has 34 words you do not know.
 *   Learn them and you will follow it standing in the masjid.
 *
 * The number it leads with is **readiness** — how much of this khutbah is
 * prepared — not points. The brief forbids fluency claims and fixes the
 * measure at "how much of this khutbah can you follow", and readiness is the
 * honest version of that: a statement about the content, answerable today,
 * rather than about a learner whose progress has nowhere to be stored.
 */

type Mode = 'week' | 'flashcards';

export function LearnScreen({ khutbahId }: { khutbahId: string }) {
  const [language, setLanguage] = useState('ml');
  const [mode, setMode] = useState<Mode>('week');

  const languages = useQuery({
    queryKey: ['languages', 'target'],
    queryFn: ({ signal }) => fetchLanguages('target', signal),
    staleTime: 60 * 60 * 1000,
  });

  const reading = useQuery({
    queryKey: readingQueryKey(khutbahId, language),
    queryFn: ({ signal }) => fetchKhutbahReading(khutbahId, language, signal),
    retry: (count, error) =>
      !(error instanceof ApiError && error.notFound) && count < 2,
  });

  const learning = useQuery({
    queryKey: ['khutbah', khutbahId, 'learning', language],
    queryFn: ({ signal }) => fetchLearning(khutbahId, language, signal),
    retry: false,
  });

  const chosen = (languages.data ?? []).find((entry) => entry.code === language);
  const scriptClass = chosen?.scriptClass ?? 'script-latin';
  const direction = chosen?.direction ?? 'ltr';
  const sourceScript = reading.data?.sourceScriptClass ?? 'script-ar';

  // Derived during render: this khutbah's glossed vocabulary, **one entry per
  // word**, in the order the khutbah first uses it.
  //
  // Deduplicated by lemma, because the index holds every occurrence: الرِّزْق
  // is glossed eleven times and العَافِيَة five, so the undeduplicated list
  // showed the same meaning five times in a row and read as a bug. It is also
  // wrong for the flashcards, which would drill one word five times and call
  // it five cards. The occurrence count is kept and shown instead — "how often
  // the khutbah says this" is itself worth knowing before Friday.
  const vocabulary: readonly { word: GlossedWord; occurrences: number }[] =
    useMemo(() => {
      const content = learning.data;
      if (!content) return [];
      const byLemma = new Map<string, { word: GlossedWord; occurrences: number }>();
      for (const word of [...content.byPassage.values()].flat()) {
        if (word.state !== 'glossed') continue;
        // Lemma is the identity; surface is a fallback for a glossed word the
        // lexicon has no lemma for, which keeps it visible rather than merging
        // unrelated words under an empty key.
        const key = word.lemma ?? `surface:${word.surface}`;
        const seen = byLemma.get(key);
        if (seen) {
          seen.occurrences += 1;
        } else {
          byLemma.set(key, { word, occurrences: 1 });
        }
      }
      return [...byLemma.values()];
    }, [learning.data]);

  const glossedWords: GlossedWord[] = useMemo(
    () => vocabulary.map((entry) => entry.word),
    [vocabulary],
  );

  // The title passage's own translation is the translated title -- the same
  // derivation the reader uses, and it inherits the same provenance.
  const titleTranslated =
    reading.data?.passages.find(
      (p) => p.sourceText.trim() === reading.data?.titleOriginal?.trim(),
    )?.translation?.text ?? null;

  const friday = reading.data?.intendedDate ?? '';
  const days = daysUntil(friday);
  const content = learning.data;

  if (reading.isPending || learning.isPending) {
    return (
      <Shell>
        <div className="flex flex-col gap-4" aria-busy>
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      </Shell>
    );
  }

  if (reading.error) {
    const api = reading.error instanceof ApiError ? reading.error : null;
    return (
      <Shell>
        <div role="alert" className="rounded-lg border border-destructive p-4">
          <p className="font-semibold">
            {api?.notFound ? 'This khutbah is not available' : 'Could not load it'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {api?.notFound
              ? 'It may not have been published yet.'
              : 'Something went wrong reaching the server.'}
          </p>
        </div>
      </Shell>
    );
  }

  if (mode === 'flashcards') {
    return (
      <Shell>
        <Flashcards
          words={glossedWords}
          scriptClass={sourceScript}
          translationScript={scriptClass}
          translationDir={direction}
          onDone={() => setMode('week')}
        />
      </Shell>
    );
  }

  const prepared = readiness(content?.glossed ?? 0, content?.words ?? 0);

  return (
    <Shell>
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          {reading.data.titleOriginal ? (
            <h1
              lang={reading.data.sourceLanguageCode}
              dir={reading.data.sourceDirection}
              className={cn(sourceScript, 'text-2xl font-normal text-reading-foreground')}
            >
              {reading.data.titleOriginal}
            </h1>
          ) : (
            // Not "This Friday's khutbah": the countdown below may say 104
            // days, and a heading that contradicts the line under it is worse
            // than a plain one.
            <h1 className="text-2xl font-semibold">Khutbah for {friday}</h1>
          )}

          {/* The topic in the reading language. A learner choosing whether to
              spend the week on this khutbah needs to know what it is about,
              and the Arabic title is the one thing they cannot yet read. */}
          {titleTranslated ? (
            <p
              lang={language}
              dir={direction}
              className={cn(scriptClass, 'text-lg text-pretty text-reading-foreground')}
            >
              {titleTranslated}
            </p>
          ) : null}

          {/* The deadline, because that is what makes this bounded. */}
          <p
            className={cn(
              'text-sm font-medium',
              days >= 0 && days <= 2 ? 'text-warning-foreground' : 'text-muted-foreground',
            )}
          >
            {countdown(friday)}
            {days < 0 ? ' · still worth studying' : null}
          </p>

          <label className="flex w-fit items-center gap-2 text-sm">
            <span className="text-muted-foreground">Learning in</span>
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            >
              {(languages.data ?? []).map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.nameEn}
                </option>
              ))}
            </select>
          </label>
        </header>

        {/* Readiness, not points. */}
        <section className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold">How much is prepared</h2>
            <span className="text-2xl font-semibold tabular-nums">{prepared}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-progress-track">
            <div className="h-full rounded-full bg-progress" style={{ width: `${prepared}%` }} />
          </div>
          <p className="mt-2 text-[13px] text-muted-foreground">
            {readinessLine(content?.glossed ?? 0, content?.words ?? 0)}
          </p>
          {/* No fluency claim, and the reason is said. */}
          <p className="mt-1 text-xs text-muted-foreground">
            This is how much of the khutbah has been prepared for learning — not
            a measure of you. Your own progress needs an account, and there are
            none yet.
          </p>
        </section>

        {glossedWords.length > 0 ? (
          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">
                The words ({vocabulary.length})
              </h2>
              <Button type="button" size="lg" onClick={() => setMode('flashcards')}>
                Flashcards
              </Button>
            </div>

            <ul className="grid gap-2 sm:grid-cols-2">
              {vocabulary.map(({ word, occurrences }) => (
                <li
                  key={word.lemma ?? `surface:${word.surface}`}
                  className="flex items-baseline justify-between gap-3 rounded-md border border-border p-3"
                >
                  <span className="flex flex-col">
                    <span
                      lang="ar"
                      dir="rtl"
                      className={cn(sourceScript, 'text-lg font-normal')}
                    >
                      {word.surface}
                    </span>
                    <span className="flex items-baseline gap-2">
                      {word.root ? (
                        <span
                          lang="ar"
                          dir="rtl"
                          className={cn(sourceScript, 'text-xs tracking-[0.3em] text-muted-foreground')}
                        >
                          {word.root}
                        </span>
                      ) : null}
                      {/* Said in words, not as a bare numeral, so it cannot be
                          misread as a verse or page reference. */}
                      {occurrences > 1 ? (
                        <span className="text-xs text-muted-foreground">
                          {occurrences}× in this khutbah
                        </span>
                      ) : null}
                    </span>
                  </span>
                  <span dir={direction} className={cn(scriptClass, 'text-end text-sm')}>
                    {word.meaning}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <EmptyLearning />
        )}

        {content && content.notes.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">Grammar</h2>
            {content.notes.map((note) => (
              <article key={note.heading} className="rounded-md border border-border p-3">
                <h3 className="text-[13px] font-semibold">{note.heading}</h3>
                <p dir={direction} className={cn(scriptClass, 'mt-1 text-sm')}>
                  {note.body}
                </p>
                {!note.reviewed ? (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Not yet checked by a reviewer.
                  </p>
                ) : null}
              </article>
            ))}
          </section>
        ) : null}

        <NotBuiltYet quizzes={content?.quizzes.length ?? 0} />

        <p className="text-xs text-muted-foreground">
          <a
            href={`?khutbah=${encodeURIComponent(khutbahId)}`}
            className="text-primary underline"
          >
            Read the khutbah
          </a>{' '}
          — every word here comes from it.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full bg-background text-foreground">
      <IconRail current="read" />
      <div className="flex min-w-0 flex-1 flex-col">
        <main className="mx-auto w-full max-w-[860px] flex-1 px-4 py-8 sm:px-6">
          {children}
        </main>
      </div>
    </div>
  );
}

function EmptyLearning() {
  return (
    <section className="rounded-lg border border-dashed border-border p-8 text-center">
      <p className="font-semibold">No words prepared yet</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-pretty text-muted-foreground">
        Nothing generates glosses yet — they are entered by hand in the editor.
        Until a word has a meaning in this language there is nothing honest to
        teach from it.
      </p>
    </section>
  );
}

/**
 * The parts of the kit with no data behind them, named rather than drawn as
 * empty shells. A screen that shows a practice section with nothing in it
 * reads as broken; one that says why reads as unfinished, which is true.
 */
function NotBuiltYet({ quizzes }: { quizzes: number }) {
  return (
    <section className="rounded-lg border border-dashed border-border p-4">
      <h2 className="text-sm font-semibold">Not built yet</h2>
      <ul className="mt-2 flex list-disc flex-col gap-1 ps-5 text-[13px] text-muted-foreground">
        <li>
          <b>Practice</b> — {quizzes === 0
            ? 'no quiz items exist for this khutbah; they are entered by hand and nothing generates them.'
            : `${quizzes} item${quizzes === 1 ? '' : 's'} exist, and the quiz player is not written.`}
        </li>
        <li>
          <b>Spoken Arabic</b> — there is no dialect column, no generation, and
          no reviewer who has agreed to check it.
        </li>
        <li>
          <b>Progress and streaks</b> — these are per person, and there are no
          accounts, so nothing about <em>how much you know</em> is kept.
          Bookmarks are the exception and no longer belong on this list: the
          library remembers what you saved and what you have read, in this
          browser.
        </li>
      </ul>
    </section>
  );
}
